import { Control as ControlType } from '@yleisradio/areena-types';
import React, { useState } from 'react';
import { isShareControl } from 'utils/control';
import { sanitizeHtmlId } from 'utils/ui-helpers';
import { ButtonPadding, ButtonSize, ButtonVariant } from 'components/Button';
import { ControlPresentation } from './ControlPresentation';
import { ControlWrapper } from './ControlWrapper/ControlWrapper';
import { Notifications } from 'components/Notifications';
import { Placeholder } from './Placeholder';
import { useControl } from './useControl';
import { LinkIconPosition, LinkTextSize, LinkTheme } from 'components/DSLink';

interface ControlProps {
  control: ControlType;
  controlKey: string;
  disabled?: boolean;
  onClick?: (() => void) | undefined;
  as: 'button' | 'link';
  padding?: ButtonPadding | undefined;
  size?: ButtonSize | undefined;
  variant?: ButtonVariant | undefined;
  width?: 'full' | undefined;
  textSize?: LinkTextSize | undefined;
  iconPosition?: LinkIconPosition | undefined;
  theme?: LinkTheme | undefined;
  dataTestId?: string;
}

export const Control: React.FunctionComponent<ControlProps> = ({
  as,
  control: controlProp,
  controlKey,
  disabled: disabledProp = false,
  onClick,
  padding,
  size,
  variant,
  width,
  textSize,
  iconPosition,
  theme,
  dataTestId,
}) => {
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const { control, action, executingAction, loading, notifications } =
    useControl({
      control: controlProp,
      isShareDialogOpen,
      setIsShareDialogOpen,
    });
  const shareDialogId = isShareControl(control)
    ? sanitizeHtmlId(`${controlKey}-dialog`)
    : undefined;

  const disabled = control?.disabled || disabledProp;

  return (
    <ControlWrapper
      control={control}
      isShareDialogOpen={isShareDialogOpen}
      setIsShareDialogOpen={setIsShareDialogOpen}
      shareDialogId={shareDialogId}
    >
      {control ? (
        <ControlPresentation
          control={control}
          disabled={disabled}
          executingAction={executingAction}
          onClick={() => {
            if (action) action();
            if (onClick) onClick();
          }}
          as={as}
          buttonPadding={padding}
          buttonSize={size}
          buttonVariant={variant}
          iconPosition={iconPosition}
          width={width}
          textSize={textSize}
          theme={theme}
          aria-expanded={
            isShareControl(control) ? isShareDialogOpen : undefined
          }
          aria-controls={shareDialogId}
          dataTestId={dataTestId}
        />
      ) : null}
      {loading ? <Placeholder size={size} /> : null}
      <Notifications
        notifications={notifications}
        notificationStyle={'transparent'}
      />
    </ControlWrapper>
  );
};
